import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import type { ComponentProps } from 'react';
import { cn } from './cn.js';

/**
 * `ai/phase-6.5-ui-polish.md` Wave 1's audit: `shell.tsx`'s org switcher and
 * account menu, and `card-tile.tsx`'s overflow menu, each built their own
 * `Content`/`Item`/`Separator` styling independently — three real instances of
 * the same `rounded border border-line bg-surface-raised p-1 shadow-lg` shell
 * and the same `data-[highlighted]:bg-surface-hover` item state, which is the
 * threshold PLAN.md §6 sets for extracting a component rather than living with
 * a fourth copy the next menu would have added.
 */
export interface DropdownMenuContentProps extends Omit<
  ComponentProps<typeof DropdownMenuPrimitive.Content>,
  'className'
> {
  readonly className?: string;
}

export function DropdownMenuContent({
  sideOffset = 4,
  className,
  ...props
}: DropdownMenuContentProps) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          /* Explicit z-index for the same reason modal.tsx's Content and
             Overlay carry one: shell.tsx's mobile sidebar backdrop (Wave 6)
             is `z-30`, and a sibling with no explicit z-index paints below
             any sibling that has one, regardless of DOM order — this menu
             (shell.tsx's own org switcher and account menu, among others)
             would otherwise render behind that backdrop on a narrow
             viewport with the sidebar open. */
          'ui-fade z-40 min-w-40 rounded border border-line bg-surface-raised p-1 shadow-lg',
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

type DropdownMenuItemTone = 'default' | 'muted' | 'danger';

/* `danger` added while consolidating platform-admin's own hand-rolled
   `RowActionsMenu` ("Delete org") onto this component
   (ai/design-rebuild-warm-dark.md §3) — the one tone this file had no
   entry for, since `card-tile.tsx`'s "Archive" reaches for `muted` even
   though archive is itself destructive-adjacent. Its own highlighted
   background differs from the other two tones' shared
   `data-[highlighted]:bg-surface-hover`, matching the danger-tinted hover
   `RowActionsMenu`'s hand-rolled button already had. */
const ITEM_TONE: Readonly<Record<DropdownMenuItemTone, string>> = {
  /* shell.tsx's own menu items, card-tile.tsx's "Archive" */
  default: 'text-ink data-[highlighted]:bg-surface-hover',
  /* shell.tsx's "All organizations…" and the signed-in email row */
  muted: 'text-ink-muted data-[highlighted]:bg-surface-hover',
  /* A destructive action — orgs-tab.tsx's "Delete org" */
  danger: 'text-danger data-[highlighted]:bg-danger/10',
};

export interface DropdownMenuItemProps extends Omit<
  ComponentProps<typeof DropdownMenuPrimitive.Item>,
  'className'
> {
  readonly tone?: DropdownMenuItemTone;
  readonly className?: string;
}

export function DropdownMenuItem({ tone = 'default', className, ...props }: DropdownMenuItemProps) {
  return (
    <DropdownMenuPrimitive.Item
      className={cn(
        'flex cursor-pointer items-center justify-between gap-3 rounded px-2 py-1.5 text-sm outline-none',
        ITEM_TONE[tone],
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator className={cn('my-1 h-px bg-line', className)} {...props} />
  );
}
